import { useContext, useEffect, useMemo, useState } from "react";

import { useLazyQuery, useQuery } from "@apollo/client";
import { Colors } from "@blueprintjs/core";
import { useLocation, useParams, useSearchParams } from "react-router";

import { useCurrentSession } from "@/components/helpers/custom-hooks/use-current-session";
import { useCurrentUser } from "@/components/helpers/custom-hooks/use-current-user";
import { useStoreSignUpParams } from "@/components/helpers/custom-hooks/use-store-signup-params";
import { useWorkspaceSlug } from "@/components/helpers/custom-hooks/use-workspace-slug";
import SEOHelmet from "@/components/helpers/seo/SEOHelmet";
import { getSignUpParams } from "@/components/helpers/signup/sign-up-params";
import { isInWorkspace } from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import Nav from "@/components/nav/Nav";
import PageContainer from "@/components/pages_flexible/PageContainer";
import Authenticate from "@/components/pages_logged_in/Authenticate";
import { NotFound } from "@/components/pages_public/404";
import CoachingProducts from "@/components/pieces/coaching/coach-profile/CoachingProducts";
import {
  checkCoachInUserCoaches,
  generateCoachSeoData,
  generateMappings,
} from "@/components/pieces/coaching/coach-profile/coachingUtils";
import BookCoach from "@/components/pieces/talent-agents/BookCoach";
import ClientComments from "@/components/pieces/talent-agents/ClientComments";
import {
  CoachProfileHeader,
  CoachProfileSkeleton,
  CoachProfileVideo,
} from "@/components/pieces/talent-agents/CoachProfile";
import ProfileDetails from "@/components/pieces/talent-agents/ProfileDetails";
import { ResponseContext } from "@/components/ResponseContext";
import { getUser } from "@/components/session/JwtTokenManager";
import {
  CARD_CLASSES,
  PUBLIC_CONTENT_MAX_WIDTH,
  SIDE_NAV_WIDTH,
} from "@/css/constants";
import {
  AgentProfileDocument,
  AgentProfileQuery,
  AgentProfileQueryVariables,
  CoachProductsDocument,
  CoachProductsQuery,
  ScheduleDocument,
  ScheduleQuery,
} from "@/graphql";

const FLOATING_PADDING = [0, 50, 75, 100];
const FLOATING_WIDTH = [0, 250, 375];
const FLOATING_FULL_WIDTH_NO_SIDEBAR = [0, 0, 450, 475];
const FLOATING_FULL_WIDTH_SIDEBAR = [0, 0, 0, 475];

interface CoachProfilePageProps {
  user?: ScheduleQuery["currentUser"];
  hasSidebar?: boolean;
}

function CoachProfilePage({ user, hasSidebar = false }: CoachProfilePageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { res } = useContext(ResponseContext);
  const loggedUser = getUser();
  let { coachSlug, specialtySlug } = useParams();
  const { storeParams } = useStoreSignUpParams();
  coachSlug = coachSlug.replace(/^@/, "");
  coachSlug = coachSlug?.toLowerCase();
  specialtySlug = specialtySlug?.toLowerCase();

  const [selectedClientId, setSelectedClientId] = useState(loggedUser?.id);

  const FLOATING_FULL_WIDTH = hasSidebar
    ? FLOATING_FULL_WIDTH_SIDEBAR
    : FLOATING_FULL_WIDTH_NO_SIDEBAR;

  const {
    data,
    loading,
    error: agentProfileError,
    refetch,
  } = useQuery<AgentProfileQuery, AgentProfileQueryVariables>(
    AgentProfileDocument,
    {
      variables: {
        coachSlug,
        customerId: selectedClientId,
        specialtySlug,
      },
    },
  );

  const profile = data?.agentProfile;
  const specialties = data?.specialties;
  const coaches = user?.coaches || [];
  const isCoachInUserCoaches = checkCoachInUserCoaches(profile, coaches);

  const [
    getCoachProducts,
    { data: coachProductsData, loading: loadingCoachProducts },
  ] = useLazyQuery<CoachProductsQuery>(CoachProductsDocument, {
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const coachProducts = coachProductsData?.coachProducts || [];

  const specialtiesWithCoachProducts = useMemo(
    () =>
      generateMappings(
        coachProducts,
        [],
        true,
        isInWorkspace() ? ["MEETING", "GROUP_MEETING"] : ["MEETING"],
      ),
    [coachProducts],
  );

  const specialtiesWithCoachProductsNoIntro = useMemo(
    () => generateMappings(coachProducts, [], false),
    [coachProducts],
  );

  const maybeAttributeReferralCode = () => {
    const signUpParams = getSignUpParams("", "", "");

    if (signUpParams.firstPage === location.pathname && !signUpParams.refcode) {
      // Create new search params with referral code
      searchParams.set("refcode", profile.user.referralCode);

      // Update URL with new search params
      setSearchParams(searchParams);

      // Store updated params
      storeParams();
    }
  };

  useEffect(() => {
    if (profile?.user) {
      if (profile.user.id) {
        getCoachProducts({
          variables: {
            coachUserId: profile?.user?.id,
            customerId: selectedClientId,
            specialtySlug,
          },
        });
      }
      if (profile.user.referralCode) {
        maybeAttributeReferralCode();
      }
    }
  }, [profile, selectedClientId]);

  const { seoTitle, seoDescription } = generateCoachSeoData(
    profile,
    specialties,
  );

  if (!profile && !loading) {
    if (res) {
      res.status(404);
    }
    return <NotFound />;
  }
  const isCoachOwner =
    profile && getUser() ? getUser().id === profile.user.id : false;

  const hideBookMeetings =
    !profile?.acceptingNewBookings ||
    (!profile?.acceptingNewClients && !isCoachInUserCoaches) ||
    !specialtiesWithCoachProducts.length;

  const availableBooking = !(
    hideBookMeetings &&
    !loadingCoachProducts &&
    !loading
  );

  const bookingCoachBox = (
    <BookCoach
      profile={profile}
      specialties={specialtiesWithCoachProducts}
      coachProducts={coachProducts}
      coachSlug={coachSlug}
      isCoachOwner={isCoachOwner}
      selectedClientId={selectedClientId}
      onChangeClient={setSelectedClientId}
      availableBooking={availableBooking}
      availableChangeSpecialties={!specialtySlug}
    />
  );

  return (
    <>
      <SEOHelmet
        title={seoTitle}
        description={seoDescription}
        canonicalDomain={
          !profile
            ? null
            : profile.primarySite === "EXEC"
              ? "https://www.exec.com"
              : "https://www.placement.com"
        }
        canonicalPath={`/@${coachSlug}`}
        bodyAttributes={{ class: "public-site" }}
        image={profile && profile.seoPhoto}
        additionalHeadTags={
          profile && !profile.seoIndex ? (
            <meta name="robots" content="noindex" />
          ) : null
        }
      />
      <Box id="sticky-container" css={{ position: "relative" as const }}>
        <Flex
          flexDirection="column"
          alignItems="center"
          css={{
            background:
              "linear-gradient(99.62deg, #003881 33.88%, #2970C9 98.39%)",
          }}
          px={[3, 4]}
          pt={4}
          pb={2}
        >
          <Flex
            width={1}
            maxWidth={PUBLIC_CONTENT_MAX_WIDTH}
            justifyContent="space-between"
            alignItems="self-start"
          >
            <Box pr={FLOATING_PADDING}>
              {profile ? (
                <CoachProfileHeader
                  profile={profile}
                  specialtySlug={specialtySlug}
                  containerCss={{
                    maxWidth: "100%",
                    borderRadius: 0,
                    color: Colors.WHITE,
                  }}
                  isProfile={true}
                  onSave={async () => {
                    await refetch();
                  }}
                  bgColor="transparent"
                />
              ) : (
                loading && (
                  <CoachProfileSkeleton
                    isFullMode={false}
                    borderClass="coach-skeleton"
                    containerCss={{ maxWidth: "100%" }}
                  />
                )
              )}
              {agentProfileError && (
                <Box p={3} color={Colors.WHITE}>
                  Something went wrong. Please refresh your browser and try
                  again.
                </Box>
              )}
            </Box>
            <Media greaterThan={hasSidebar ? "md" : "sm"}>
              <Box width={FLOATING_WIDTH} height={0}>
                <Box
                  css={{
                    position: "sticky",
                    top: "70px",
                    zIndex: 1,
                  }}
                >
                  {bookingCoachBox}
                </Box>
              </Box>
            </Media>
          </Flex>
        </Flex>
        <Flex
          flexDirection="column"
          alignItems="center"
          px={[3, 4]}
          mb={120}
          pt={4}
        >
          <Box
            width={1}
            maxWidth={PUBLIC_CONTENT_MAX_WIDTH}
            pr={FLOATING_FULL_WIDTH}
          >
            {profile && (
              <>
                <Media lessThan={hasSidebar ? "lg" : "md"}>
                  {bookingCoachBox}
                </Media>
                <CoachProfileVideo profile={profile} />
                <ProfileDetails isProfile={true} profile={profile} />
                <ClientComments
                  profile={profile}
                  className={CARD_CLASSES}
                  backgroundColor="#FFF"
                  px={[3, 3, 3, 4]}
                  py={3}
                  my={0}
                  enableRatingMenu={
                    (user &&
                      profile &&
                      profile.user &&
                      user.id == profile.user.id) ||
                    (loggedUser &&
                      (loggedUser.isStaff || loggedUser.isSuperuser))
                  }
                />
                <CoachingProducts
                  backgroundColor="#FFF"
                  agentProfile={profile}
                  specialties={specialtiesWithCoachProductsNoIntro}
                  isCoachInUserCoaches={isCoachInUserCoaches}
                  isLoading={!profile.user}
                  asCoach={false}
                  showLinks={true}
                />
              </>
            )}
          </Box>
        </Flex>
      </Box>
    </>
  );
}

function AuthCoachProfilePage() {
  const { data } = useQuery<ScheduleQuery>(ScheduleDocument, {
    fetchPolicy: "network-only",
  });

  return (
    <>
      <Nav />
      <Box ml={[0, 0, SIDE_NAV_WIDTH]}>
        <CoachProfilePage user={data && data.currentUser} hasSidebar={true} />
      </Box>
    </>
  );
}

export default function DefaultPage() {
  const { currentSessionLink } = useCurrentSession();
  const workspaceSlug = useWorkspaceSlug();
  const currentUser = useCurrentUser();

  if (import.meta.env.SSR && (workspaceSlug || currentUser)) {
    return null;
  }

  if (!import.meta.env.SSR && (currentSessionLink || workspaceSlug)) {
    return (
      <Authenticate maxWidth="100%" layout="profile">
        <AuthCoachProfilePage />
      </Authenticate>
    );
  }

  // Only support SSR for Public Coach Profile with no workspace
  return (
    <PageContainer maxWidth="100%">
      <CoachProfilePage user={undefined} />
    </PageContainer>
  );
}
